import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useState, type FormEvent } from "react";
import { RUNS_PAGE_SIZE } from "../api/usageTracking";
import { useUsageRuns } from "../hooks/useUsageTracking";
import type { RunType } from "../types";
import { RUN_TYPE_LABELS } from "../utils/format";
import { RunsTable } from "./RunsTable";

const RUN_TYPES = Object.keys(RUN_TYPE_LABELS) as RunType[];
const FIELD_LABEL = "mb-1 block text-xs font-medium text-zinc-600";

export const RunsSection = ({
  days,
  onOpenRun,
}: {
  days: number;
  onOpenRun: (runId: string) => void;
}) => {
  const [runType, setRunType] = useState<RunType | null>(null);
  const [userDraft, setUserDraft] = useState("");
  const [user, setUser] = useState("");
  // A new filter starts again at the newest runs.
  const filterKey = `${days}|${runType ?? ""}|${user}`;
  const [page, setPage] = useState({ key: filterKey, offset: 0 });
  const offset = page.key === filterKey ? page.offset : 0;
  const runs = useUsageRuns({ days, runType, user, offset });
  const rows = runs.data?.runs ?? [];

  const goTo = (next: number) => setPage({ key: filterKey, offset: Math.max(0, next) });
  const searchUser = (event: FormEvent) => {
    event.preventDefault();
    setUser(userDraft.trim());
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="usage-run-type" className={FIELD_LABEL}>
            Run type
          </label>
          <select
            id="usage-run-type"
            className="h-9 rounded-[3px] border border-zinc-200 bg-white px-2 text-sm text-zinc-900"
            value={runType ?? ""}
            onChange={(event) => setRunType((event.target.value || null) as RunType | null)}
          >
            <option value="">All run types</option>
            {RUN_TYPES.map((type) => (
              <option key={type} value={type}>
                {RUN_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
        <form onSubmit={searchUser} className="flex items-end gap-2">
          <div>
            <label htmlFor="usage-run-user" className={FIELD_LABEL}>
              User email or ID
            </label>
            <Input
              id="usage-run-user"
              value={userDraft}
              maxLength={200}
              onChange={(event) => setUserDraft(event.target.value)}
              placeholder="name@example.com"
              className="h-9 w-64"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9">
            Search
          </Button>
        </form>
      </div>

      {runs.isLoading ? (
        <p className="text-sm text-zinc-500" aria-live="polite">
          Loading the runs…
        </p>
      ) : runs.isError ? (
        <p role="alert" className="text-sm text-zinc-600">
          Could not load the runs. Reload the page to try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-zinc-500">No runs match these filters.</p>
      ) : (
        <>
          <RunsTable runs={rows} onOpen={onOpenRun} />
          <nav aria-label="Pages of runs" className="flex items-center justify-between gap-3">
            <p className="text-xs text-zinc-500" aria-live="polite">
              Runs {offset + 1}–{offset + rows.length}, newest first
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => goTo(offset - RUNS_PAGE_SIZE)}
              >
                Newer
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!runs.data?.has_more}
                onClick={() => goTo(offset + RUNS_PAGE_SIZE)}
              >
                Older
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
};
